/**
 * Servicio de onboarding: crear solicitudes con Auth (cliente y empresa),
 * match empresa por RUT/web, actualizar usuario de forma segura.
 */
import { doc, setDoc, addDoc, collection, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { db, auth } from '../../../firebase';
import { signUpEmail, ensureUserDoc } from './authService';
import { sanitizeForFirestoreWrite } from '../../../utils/empresaStandards';

/**
 * Normaliza string para búsqueda (lowercase, sin espacios extra).
 */
function normalize(str) {
  return String(str || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Crea cuenta Auth + doc usuarios + solicitud cliente. Redirigir a /pendiente después.
 * @param {{ email: string, password: string, nombres: string, apellidos: string, ...formData }} formData
 * @returns {Promise<{ success: boolean, uid?: string, solicitudId?: string, error?: string, code?: string }>}
 */
export async function createSolicitudClienteWithAuth(formData) {
  const email = (formData.email || '').trim().toLowerCase();
  const password = formData.password;
  if (!email || !password) {
    return { success: false, error: 'Email y contraseña son requeridos', code: 'missing_fields' };
  }

  const { success, user, error, code } = await signUpEmail({ email, password });
  if (!success || !user) {
    return { success: false, error: error || 'Error al crear la cuenta', code };
  }

  const uid = user.uid;
  const displayName = [formData.nombres, formData.apellidos].filter(Boolean).join(' ') || email;

  try {
    await ensureUserDoc(uid, {
      uid,
      email,
      displayName,
      rol: 'cliente',
      accountType: 'cliente',
      status: 'pendiente',
      createdAt: serverTimestamp(),
      lastLoginAt: serverTimestamp(),
    });

    const solicitudData = {
      uid,
      email,
      nombres: formData.nombres,
      apellidos: formData.apellidos,
      rut: formData.rut,
      telefono: formData.telefono?.length === 9 ? '+56' + formData.telefono : formData.telefono,
      direccion: formData.direccion,
      comuna: formData.comuna,
      ciudad: formData.ciudad,
      region: formData.region,
      fecha_nacimiento: formData.fecha_nacimiento,
      profesion: formData.profesion,
      empresa: formData.empresa,
      motivo_registro: formData.motivo_registro,
      servicios_interes: formData.servicios_interes || [],
      acepta_terminos: formData.acepta_terminos,
      acepta_notificaciones: formData.acepta_notificaciones !== false,
      estado_general: 'enviada',
      etapa_actual: 'validacion_datos',
      progreso_porcentaje: 0,
      status: 'pendiente',
      fecha_solicitud: new Date(),
      etapas: {
        validacion_datos: { estado: 'pendiente', fecha_inicio: new Date(), fecha_fin: null, comentarios: '', responsable: null },
        confirmacion_final: { estado: 'pendiente', fecha_inicio: null, fecha_fin: null, comentarios: '', responsable: null },
      },
      ip_registro: typeof window !== 'undefined' ? window.location.hostname : '',
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      referencia: 'web_directa',
      tipo_registro: 'email_password',
    };

    const docRef = await addDoc(collection(db, 'solicitudes_cliente'), solicitudData);
    // Cerrar sesión inmediatamente: el usuario debe esperar la activación por el admin.
    // Firebase auto-loguea al crear una cuenta con email/contraseña, hay que revertirlo.
    await signOut(auth).catch(() => {});
    return { success: true, uid, solicitudId: docRef.id };
  } catch (err) {
    return { success: false, error: err.message || 'Error al guardar la solicitud', code: 'firestore_error' };
  }
}

/**
 * Busca empresas por RUT o página web normalizada (para match/claim).
 * @param {{ rutEmpresa?: string, paginaWeb?: string }} params
 * @returns {Promise<{ empresaId?: string, matchScore?: number }>}
 */
export async function matchEmpresaByRutWeb(rutEmpresa, paginaWeb) {
  const rutNorm = normalize((rutEmpresa || '').replace(/[^0-9kK]/g, ''));
  const webNorm = normalize((paginaWeb || '').replace(/^https?:\/\//, '').split('/')[0]);
  if (!rutNorm && !webNorm) return {};

  const empresasRef = collection(db, 'empresas');
  let empresaId = null;
  let matchScore = 0;

  if (rutNorm) {
    const q = query(empresasRef, where('rut', '==', rutNorm));
    const snap = await getDocs(q);
    if (!snap.empty) {
      empresaId = snap.docs[0].id;
      matchScore = 1;
      return { empresaId, matchScore };
    }
  }
  if (webNorm) {
    const snap = await getDocs(empresasRef);
    for (const d of snap.docs) {
      const data = d.data();
      const empWeb = normalize((data.pagina_web || data.paginaWeb || data.web_actual || '').replace(/^https?:\/\//, '').split('/')[0]);
      if (empWeb && empWeb === webNorm) {
        empresaId = d.id;
        matchScore = 0.8;
        return { empresaId, matchScore };
      }
    }
  }
  return {};
}

/**
 * Crea solicitud empresa SIN Auth (sin contraseña). El agente crea credenciales al activar.
 * Si se pasa solicitudDataCompleto, se usa como base y se agregan match + estado.
 * Redirigir a /empresa/pendiente después.
 * @param {Object} formData - datos del formulario (email_empresa, rut_empresa, etc.)
 * @param {Object} [solicitudDataCompleto] - payload completo (representante, datos_solicitud, etc.)
 */
export async function createSolicitudEmpresaSinAuth(formData, solicitudDataCompleto) {
  const email = (formData.email_empresa || formData.email || '').trim().toLowerCase();
  if (!email) {
    return { success: false, error: 'Email de la empresa es requerido', code: 'missing_fields' };
  }

  const match = await matchEmpresaByRutWeb(formData.rut_empresa, formData.web_actual || formData.paginaWeb);

  try {
    const matchFields = match.empresaId
      ? { empresaIdSugerida: match.empresaId, matchScore: match.matchScore }
      : {};

    const baseDoc = solicitudDataCompleto
      ? {
          ...solicitudDataCompleto,
          uid: null,
          ...matchFields,
          status: 'pendiente',
          estado: 'ingresada',
          fecha_solicitud: solicitudDataCompleto.fecha_solicitud ?? new Date(),
        }
      : {
          email: email || '',
          rutEmpresa: formData.rut_empresa ?? '',
          paginaWeb: formData.web_actual || formData.paginaWeb || '',
          telefono: formData.telefono_empresa ?? '',
          razonSocial: formData.nombre_empresa || formData.razonSocial || '',
          nombre_empresa: formData.nombre_empresa ?? '',
          direccion_empresa: formData.direccion_empresa ?? '',
          comuna: formData.comuna ?? '',
          region: formData.region ?? '',
          status: 'pendiente',
          estado: 'ingresada',
          fecha_solicitud: new Date(),
          ...matchFields,
        };

    // Registro proveedor pasa solicitudDataCompleto con datos_solicitud (descripción, web, redes, etc.).
    // omitDatosSolicitud borraba ese objeto entero → admin veía "No especificado". Solo omitir en el flujo mínimo (sin payload completo).
    const docRef = await addDoc(
      collection(db, 'solicitudes_empresa'),
      sanitizeForFirestoreWrite(baseDoc, { omitDatosSolicitud: !solicitudDataCompleto })
    );
    return { success: true, solicitudId: docRef.id };
  } catch (err) {
    return { success: false, error: err.message || 'Error al guardar la solicitud', code: 'firestore_error' };
  }
}
